/* eslint-disable no-console */
/**
 * Hook de carga de la capa unificada de documentos de costo (Fase 3a/3b).
 *
 * Trae las tres fuentes + los índices de pago y devuelve los documentos con sus dos ejes ya
 * derivados. Toda la lógica vive en `documentosCosto.js` (función pura, testeada); acá solo
 * está el transporte y el armado del ledger.
 *
 * COSTO DEL EJE DE PAGO. El estado "Cubierta a cuenta" sale del ledger de proveedor, que se
 * calcula POR proveedor — pero eso NO implica una query por proveedor: `construirLedgerProveedor`
 * es una función pura sobre arrays. Se traen los insumos crudos UNA vez (5 queries extra) y se
 * itera en memoria, exactamente el patrón de FlujoCajaV2 (`index.js:1069-1110`).
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { buildContextoLibro } from "../cobro/resolverSaldo";
import { construirLedgerProveedor } from "../estadoCuenta/ledgerProveedor";
import { FECHA_CORTE } from "../estadoCuenta/saldoInicial";
import { getDolarValue, getDolarFallback, loadExchangeRates } from "../exchangeRateService";
import { client } from "../supabase";
import { construirDocumentosCosto, indexarEventosLibroPago, indexarCuotas } from "./documentosCosto";

const VACIO = [];

/**
 * Corre el ledger de cada proveedor con actividad y devuelve el mapa de saldos vivos + el set
 * de proveedores efectivamente calculados. Los dos se necesitan: la AUSENCIA de una clave en
 * el mapa significa "saldada" solo si ese proveedor entró al ledger (regla 10.106 + 10.70).
 */
const construirSaldosFifo = ({
  recibidas,
  externas,
  honorarios,
  movimientosCaja,
  saldosIniciales,
  proveedores,
  mbf,
  provIdsEdificio,
  monedaLocal,
  pais,
  ctxLibro,
}) => {
  const saldosFifo = new Map();
  const proveedoresConLedger = new Set();

  // Solo desde FECHA_CORTE: las pre-corte ya están en el saldo inicial por entidad; incluirlas
  // las doble-contaría y mis-atribuiría pagos 2026 a facturas viejas (mismo criterio que 4.2.dd).
  // NOTA Pieza A (regla 10.29): acá NO se "despiertan" las pre-corte con residual_corte (a diferencia de
  // FlujoCajaV2 / EstadoCuenta) A PROPÓSITO — `crudo.sii` alimenta también la LISTA de documentos de
  // costo, y destaparlas las haría aparecer como documentos nuevos en Compras. Su deuda sigue agregada en
  // el INI; el estado de los documentos POST-corte no cambia (mismo total). Ver ledgerProveedor.js.
  const recibidasArr = (recibidas || []).filter((f) => !f.fecha_emision || f.fecha_emision >= FECHA_CORTE);
  // BHE (Fase 1): mismo corte que las recibidas. El ledger las modela como deuda al monto_bruto.
  const honorariosArr = (honorarios || []).filter((b) => !b.fecha_emision || b.fecha_emision >= FECHA_CORTE);

  const provIds = new Set();
  const sumar = (id) => {
    if (id && !provIdsEdificio.has(id)) provIds.add(id);
  };
  recibidasArr.forEach((f) => sumar(f.proveedor_id));
  (externas || []).forEach((f) => sumar(f.proveedor_id));
  // Un proveedor puede tener SOLO BHE (sin SII/EXT/caja): igual debe entrar al ledger para que su
  // deuda de honorarios se compute y su clave BHE:<id> emita saldo (sin esto quedaría fuera y la
  // BHE recaería en el falso "Cubierta a cuenta").
  honorariosArr.forEach((b) => sumar(b.proveedor_id));
  (movimientosCaja || []).forEach((m) => {
    if (m.entidad_tipo === "proveedor") sumar(m.entidad_id);
  });

  // Mismos conversores que FlujoCajaV2: cada proveedor liquida en SU moneda operativa
  // (`moneda_default`), para no arrastrar wobble de doble conversión en el FIFO.
  const tdcLocal = (monto, moneda, fecha) => {
    if (!monto) return 0;
    const origen = moneda || monedaLocal;
    if (origen === monedaLocal) return monto;
    if (origen === "USD") return Math.round(monto * (getDolarValue(fecha, pais) || getDolarFallback(pais) || 1));
    return monto;
  };
  const tdcUSD = (monto, moneda, fecha) => {
    if (!monto) return 0;
    const origen = moneda || monedaLocal;
    if (origen === "USD") return monto;
    if (origen === monedaLocal) {
      const rate = getDolarValue(fecha, pais) || getDolarFallback(pais) || 1;
      return rate > 0 ? Math.round(monto / rate) : 0;
    }
    return monto;
  };

  provIds.forEach((provId) => {
    const prov = (proveedores || []).find((p) => p.id === provId);
    const esUSD = (prov?.moneda_default || monedaLocal) === "USD";
    const { saldosPorFactura } = construirLedgerProveedor({
      provId,
      siiRecibidas: recibidasArr,
      facturasExternas: externas || [],
      honorarios: honorariosArr,
      movimientosCaja: movimientosCaja || [],
      saldoInicial:
        (saldosIniciales || []).find((s) => s.entidad_tipo === "proveedor" && s.entidad_id === provId) || null,
      monedaPrincipal: monedaLocal,
      vinculacionesDirectas: mbf || [],
      excluirProvIds: provIdsEdificio,
      toDisplayCurrency: esUSD ? tdcUSD : tdcLocal,
      ctxLibro, // R3.2: la saldada/saldo por factura sale del libro (resolverSaldo)
    });
    // El proveedor-edificio devuelve `[]` (no `{saldosPorFactura}`) — queda fuera del set,
    // que es justo lo que corresponde: su deuda no se mide como proveedor (regla 10.70).
    if (!saldosPorFactura) return;
    proveedoresConLedger.add(provId);
    saldosPorFactura.forEach((valor, clave) => {
      if (clave !== "RESIDUO") saldosFifo.set(clave, valor);
    });
  });

  return { saldosFifo, proveedoresConLedger };
};

export const useDocumentosCosto = (pais, { desde = FECHA_CORTE } = {}) => {
  const [crudo, setCrudo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  const recargar = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!pais || pais === "Region") return undefined;
    let cancelado = false;

    const cargar = async () => {
      setCargando(true);
      try {
        await loadExchangeRates();
        const [sii, externas, honorarios, mbf, cuotas, movsCaja, saldosIni, proveedores, edificios, activos, libro] =
          await Promise.all([
            client.from("facturas_sii_recibidas").select("*").eq("pais", pais).gte("fecha_emision", desde),
            client.from("facturas_externas").select("*").eq("pais", pais).neq("estado", "anulada"),
            client.from("boletas_honorarios").select("*").eq("pais", pais).gte("fecha_emision", desde),
            // MBF es polimórfica y NO tiene columna `pais`: se trae completa y se indexa por
            // clave. Las vinculaciones de otro país no matchean con ningún documento.
            client
              .from("movimiento_bancario_facturas")
              .select("movimiento_bancario_id, factura_tipo, factura_id, monto"),
            client.from("plan_pagos_facturas").select("*").eq("pais", pais),
            // Insumos del ledger (una pasada, no una query por proveedor).
            client.from("movimientos_caja").select("*").eq("pais", pais).eq("entidad_tipo", "proveedor"),
            client.from("saldos_iniciales").select("*").eq("pais", pais).eq("entidad_tipo", "proveedor"),
            client.from("proveedores").select("id, nombre, moneda_default").eq("pais", pais),
            client.from("edificios").select("proveedor_id").eq("pais", pais),
            // C2 — eje "Activo vinculado": activos vivos, para saber si un documento clasificado
            // 'activo' cuelga de un registro real en `activos` o quedó suelto.
            client
              .from("activos")
              .select("boleta_honorario_id, factura_sii_recibida_id")
              .eq("pais", pais)
              .eq("eliminado", false),
            // R3.2 — libro del eje pago para que el ledger derive la saldada/saldo de resolverSaldo
            // (regla 10.190). aplicaciones_saldo NO es país-scoped: se filtra por documento_tipo.
            client
              .from("aplicaciones_saldo")
              .select("documento_tipo, documento_id, via, monto_documento, moneda_origen, fecha_evento")
              .in("documento_tipo", ["sii_recibida", "factura_externa", "boleta_honorario"]),
          ]);

        if (cancelado) return;
        const primerError = [
          sii,
          externas,
          honorarios,
          mbf,
          cuotas,
          movsCaja,
          saldosIni,
          proveedores,
          edificios,
          activos,
          libro,
        ].find((r) => r?.error)?.error;
        if (primerError) throw primerError;

        // Set de claves de documento que YA cuelgan de un activo. Mismo esquema de clave que
        // documentosCosto (BHE:<id> / SII:<id>). Las externas no tienen tipo_contable → no aplican.
        const activosVinculados = new Set();
        for (const a of activos.data || []) {
          if (a.boleta_honorario_id != null) activosVinculados.add(`BHE:${a.boleta_honorario_id}`);
          if (a.factura_sii_recibida_id != null) activosVinculados.add(`SII:${a.factura_sii_recibida_id}`);
        }

        setCrudo({
          sii: sii.data || VACIO,
          externas: externas.data || VACIO,
          honorarios: honorarios.data || VACIO,
          // MBF sigue haciendo falta: es insumo del FIFO (`vinculacionesDirectas` en
          // construirLedgerProveedor, vía construirSaldosFifo abajo) — el FIFO NO se retira en
          // R3.4. Ya NO alimenta `montoVinculadoPorDocumento` (eso ahora sale del libro, ver
          // `documentos` más abajo).
          mbf: mbf.data || VACIO,
          cuotas: cuotas.data || VACIO,
          movsCaja: movsCaja.data || VACIO,
          saldosIni: saldosIni.data || VACIO,
          proveedores: proveedores.data || VACIO,
          provIdsEdificio: new Set((edificios.data || []).map((e) => e.proveedor_id).filter((id) => id != null)),
          activosVinculados,
          libro: libro.data || VACIO,
          ctxLibro: buildContextoLibro(libro.data || []),
        });
        setError(null);
      } catch (err) {
        if (!cancelado) {
          console.error("useDocumentosCosto: error cargando documentos de costo", err);
          setError(err);
        }
      } finally {
        if (!cancelado) setCargando(false);
      }
    };

    cargar();
    return () => {
      cancelado = true;
    };
  }, [pais, desde, nonce]);

  const documentos = useMemo(() => {
    if (!crudo) return VACIO;
    const monedaLocal = pais === "Perú" ? "PEN" : "CLP";
    const { saldosFifo, proveedoresConLedger } = construirSaldosFifo({
      recibidas: crudo.sii,
      externas: crudo.externas,
      honorarios: crudo.honorarios,
      movimientosCaja: crudo.movsCaja,
      saldosIniciales: crudo.saldosIni,
      proveedores: crudo.proveedores,
      mbf: crudo.mbf,
      provIdsEdificio: crudo.provIdsEdificio,
      monedaLocal,
      pais,
      ctxLibro: crudo.ctxLibro,
    });
    return construirDocumentosCosto(
      {
        siiRecibidas: crudo.sii,
        externas: crudo.externas,
        honorarios: crudo.honorarios,
        proveedores: crudo.proveedores,
      },
      {
        montoVinculadoPorDocumento: indexarEventosLibroPago(crudo.libro),
        cuotasPorDocumento: indexarCuotas(crudo.cuotas),
        saldosFifo,
        proveedoresConLedger,
        activosVinculados: crudo.activosVinculados,
      }
    );
  }, [crudo, pais]);

  return { documentos, cargando, error, recargar };
};
